import { useContext } from 'react';
import { Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { AppContext } from '../../sporttia/all';
import generalConfig from '../../config/general';
import { writeLocaleCookie } from '../../lib/localeCookie';

/**
 * Selector de idioma dentro de los ajustes. Reutiliza la lista de idiomas de
 * la configuración general y persiste la preferencia en el perfil como hacía
 * el selector del pie.
 */
export function LanguageSetting(): React.ReactElement {
    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();

    const selectLanguage = (
        language: (typeof generalConfig)['languages'][number],
    ): void => {
        cxt.setLang(language);
        // La misma cookie que escribe competitions, para que el idioma no
        // cambie al pasar de una aplicación a la otra.
        writeLocaleCookie(language.key);

        if (cxt.logged) {
            cxt.api('PUT', '/me', { params: { lang: language.key } });
        }
    };

    return (
        <section className="flex flex-col gap-3">
            <h3 className="m-0 text-sm font-medium">
                {t('settings.language.label')}
            </h3>
            <div className="grid grid-cols-2 gap-2">
                {generalConfig.languages.map((language) => {
                    const isActive = cxt?.lang?.key === language.key;

                    return (
                        <button
                            key={language.key}
                            type="button"
                            aria-pressed={isActive}
                            onClick={() => selectLanguage(language)}
                            className={`flex cursor-pointer items-center gap-2 rounded-lg border p-3 text-sm transition-colors ${
                                isActive
                                    ? 'bg-primary text-primary-foreground border-primary-foreground font-medium'
                                    : 'border-border text-foreground hover:bg-accent'
                            }`}
                        >
                            {/* `flag` viene de la configuración con los mismos
                                códigos de flag-icons que competitions. Nunca se
                                deriva de `key`: `gl` sería Groenlandia y `ca`
                                Canadá en lugar de Galicia y Cataluña. */}
                            <span className={`fi fi-${language.flag}`} />
                            <span className="flex-1 text-left">
                                {language.caption}
                            </span>
                            {isActive && <Check className="h-4 w-4" />}
                        </button>
                    );
                })}
            </div>
        </section>
    );
}
